import React from 'react';
import { Box, Typography, Button, Chip, alpha, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { Link } from 'react-router-dom';
import { MOCK_INTERVIEW_BOOKING_PRICE } from '../common/constants';

const ACCENT = '#137274';
const PINK = '#f50057';
const formatInr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;
const mockPrice = formatInr(MOCK_INTERVIEW_BOOKING_PRICE);

/**
 * Compact industry-mentorship promo for home, trackers, interview prep, etc.
 * @param {'banner' | 'compact' | 'strip'} variant
 */
const MentorshipCallout = ({ variant = 'banner' }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';

  if (variant === 'strip') {
    return (
      <Box
        component={Link}
        to="/mentorship"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.35,
          borderRadius: 2,
          textDecoration: 'none',
          color: 'inherit',
          border: `1px solid ${alpha(ACCENT, 0.35)}`,
          background: isDark
            ? `linear-gradient(90deg, ${alpha(ACCENT, 0.22)}, ${alpha(PINK, 0.08)})`
            : `linear-gradient(90deg, ${alpha(ACCENT, 0.1)}, ${alpha(PINK, 0.05)})`,
          transition: 'transform 0.15s, border-color 0.15s',
          '&:hover': {
            transform: 'translateY(-1px)',
            borderColor: ACCENT,
          },
        }}
      >
        <WorkspacePremiumOutlinedIcon sx={{ color: ACCENT, fontSize: 22, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', letterSpacing: '-0.01em' }}>
            1:1 with a FAANG SDE · guidance from ₹1,199
          </Typography>
          <Typography variant="caption" sx={{ color: muted, display: 'block' }}>
            Full mentorship, 1–2 hr guidance, or a {mockPrice} mock — DSA, System Design & CS
          </Typography>
        </Box>
        <ArrowForwardIcon sx={{ color: ACCENT, fontSize: 18, flexShrink: 0 }} />
      </Box>
    );
  }

  if (variant === 'compact') {
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 2.5,
          border: `1px solid ${border}`,
          bgcolor: surface,
          background: isDark
            ? `linear-gradient(135deg, ${alpha(ACCENT, 0.2)}, transparent 70%)`
            : `linear-gradient(135deg, ${alpha(ACCENT, 0.08)}, transparent 70%)`,
        }}
      >
        <Chip
          size="small"
          label="Industry mentor"
          sx={{
            mb: 1,
            height: 22,
            fontWeight: 800,
            fontSize: '0.65rem',
            bgcolor: alpha(ACCENT, isDark ? 0.25 : 0.12),
            color: ACCENT,
          }}
        />
        <Typography sx={{ fontWeight: 800, fontSize: '1rem', letterSpacing: '-0.02em', mb: 0.5 }}>
          Stuck? Get a FAANG SDE in your corner
        </Typography>
        <Typography variant="body2" sx={{ color: muted, mb: 1.5, fontSize: '0.82rem', lineHeight: 1.45 }}>
          Daily connect · follow-ups · DSA + System Design + CS — or a ₹1,199 guidance call / {mockPrice}
          mock.
        </Typography>
        <Button
          component={Link}
          to="/mentorship"
          size="small"
          endIcon={<ArrowForwardIcon />}
          sx={{ textTransform: 'none', fontWeight: 800, color: ACCENT, px: 0 }}
        >
          View mentorship
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2.5, sm: 3.25 },
        borderRadius: 3,
        border: `1px solid ${alpha(ACCENT, 0.3)}`,
        overflow: 'hidden',
        position: 'relative',
        background: isDark
          ? `radial-gradient(700px 280px at 0% 0%, ${alpha(ACCENT, 0.35)}, transparent 55%), radial-gradient(500px 220px at 100% 100%, ${alpha(PINK, 0.12)}, transparent 50%), #161b22`
          : `radial-gradient(700px 280px at 0% 0%, ${alpha(ACCENT, 0.16)}, transparent 55%), radial-gradient(500px 220px at 100% 100%, ${alpha(PINK, 0.08)}, transparent 50%), #fff`,
      }}
    >
      <Chip
        size="small"
        icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: '14px !important' }} />}
        label="FAANG SDE · DSA expert"
        sx={{
          mb: 1.5,
          fontWeight: 800,
          bgcolor: alpha(ACCENT, isDark ? 0.28 : 0.12),
          color: ACCENT,
          '& .MuiChip-icon': { color: ACCENT },
        }}
      />
      <Typography
        sx={{
          fontWeight: 900,
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          letterSpacing: '-0.03em',
          mb: 0.75,
          maxWidth: 520,
        }}
      >
        Complete interview prep with 1:1 mentorship
      </Typography>
      <Typography
        sx={{
          color: muted,
          mb: 2,
          maxWidth: 520,
          fontSize: '0.92rem',
          lineHeight: 1.55,
        }}
      >
        6–7 month planner: DSA, HLD, LLD & CS — plus resume prep and referrals (not job guarantees).
        Prefer a one-off? Book a ₹1,199 guidance call or a {mockPrice} mock.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Button
          component={Link}
          to="/mentorship"
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            bgcolor: ACCENT,
            '&:hover': { bgcolor: '#0f5c5e' },
          }}
        >
          Explore mentorship
        </Button>
        <Typography variant="caption" sx={{ color: muted, fontWeight: 700 }}>
          ₹11,999/mo · ₹59,999 for 6–7 months · ₹1,199 guidance · {mockPrice} mock
        </Typography>
      </Box>
    </Box>
  );
};

export default MentorshipCallout;
